<script setup>
import PurchaseItemsTable from '@/components/purchase/PurchaseItemsTable.vue'
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Purchase',
  },
})

const router = useRouter()
const { loading, errors, request } = useApiRequest()

// ─── Form ─────────────────────────────────────────────────────────────────────
const form = ref({
  supplier_id:  null,
  expected_at:  '',
  notes:        '',
})

// Shape generik dipakai PurchaseItemsTable — lihat catatan di komponennya.
const items = ref([
  { ingredient_id: null, unit_id: null, quantity: 1, total_price: 0 },
])

// ─── Dropdown options ─────────────────────────────────────────────────────────
const suppliers    = ref([])
const ingredients  = ref([])
const units        = ref([])
const optLoading   = ref(true)

onMounted(async () => {
  try {
    const [sRes, iRes, uRes] = await Promise.all([
      useApi('/api/v1/suppliers', { query: { per_page: 200, is_active: 1 } }).get(),
      useApi('/api/v1/ingredients', { query: { per_page: 500 } }).get(),
      useApi('/api/v1/units', { query: { per_page: 200 } }).get(),
    ])

    // Masing-masing return body langsung dari ofetch
    const sBody = sRes?.data ?? sRes
    const iBody = iRes?.data ?? iRes
    const uBody = uRes?.data ?? uRes

    suppliers.value = (Array.isArray(sBody) ? sBody : (sBody?.data ?? [])).map(s => ({ title: s.name, value: s.id }))

    // unitId = satuan dasar ingredient ini sendiri — dipakai PurchaseItemsTable
    // utk filter pilihan "Satuan Pembelian" (satuan dasar + turunannya saja).
    ingredients.value = (Array.isArray(iBody) ? iBody : (iBody?.data ?? [])).map(i => ({
      title: i.name,
      value: i.id,
      unitId: i.unit?.id ?? null,
    }))

    // Satuan TIDAK di-map ke {title,value} — PurchaseItemsTable butuh field
    // aslinya (name, symbol, base_unit_id, conversion_factor) utk konversi.
    units.value = Array.isArray(uBody) ? uBody : (uBody?.data ?? [])
  } catch (err) {
    console.error('[POCreate] loadOptions error', err)
  } finally {
    optLoading.value = false
  }
})

// ─── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  const payload = {
    ...form.value,
    items: items.value.map(i => ({
      ingredient_id: i.ingredient_id,
      unit_id: i.unit_id,
      quantity_ordered: i.quantity,
      total_price: i.total_price,
    })),
  }

  const result = await request(() =>
    useApi('/api/v1/purchase-orders').post({ body: payload })
  )

  if (result !== null) {
    // result = body langsung; backend wrap dengan { success, data, message }
    const body = result?.data ?? result
    const id = body?.id ?? body?.data?.id
    router.push({ name: 'purchase-orders-id', params: { id } })
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="pt-5 px-5">Buat Purchase Order</VCardTitle>

        <VDivider />

        <VCardText class="pa-5">
          <!-- General error -->
          <VAlert
            v-if="errors._general"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ errors._general[0] }}
          </VAlert>

          <!-- Header PO -->
          <VRow class="mb-2">
            <VCol cols="12" md="5">
              <VAutocomplete
                v-model="form.supplier_id"
                :items="suppliers"
                :loading="optLoading"
                label="Supplier *"
                :error-messages="errors.supplier_id"
                no-data-text="Tidak ada supplier aktif"
              />
            </VCol>
            <VCol cols="12" md="3">
              <VTextField
                v-model="form.expected_at"
                label="Estimasi Tiba"
                type="date"
                :error-messages="errors.expected_at"
              />
            </VCol>
            <VCol cols="12" md="4">
              <VTextarea
                v-model="form.notes"
                label="Catatan"
                rows="2"
                auto-grow
                :error-messages="errors.notes"
              />
            </VCol>
          </VRow>

          <!-- Items -->
          <div class="text-subtitle-2 font-weight-semibold mb-3 mt-1">
            Item Pesanan
          </div>

          <PurchaseItemsTable
            v-model="items"
            :ingredients="ingredients"
            :units="units"
            :errors="errors"
            :loading="optLoading"
            quantity-label="Qty"
          />
        </VCardText>

        <VDivider />

        <VCardActions class="pa-5">
          <VSpacer />
          <VBtn variant="tonal" :to="{ name: 'purchase-orders' }">Batal</VBtn>
          <VBtn color="primary" :loading="loading" @click="submit">
            Simpan PO
          </VBtn>
        </VCardActions>
      </VCard>
    </VCol>
  </VRow>
</template>